import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  Phone, 
  Clock, 
  User, 
  Download, 
  RefreshCw,
  ArrowLeft,
  FileText,
  MessageSquare,
  Play,
  Pause,
  PhoneIncoming,
  PhoneOutgoing,
  DollarSign,
  UserCircle
} from 'lucide-react';
import axios from 'axios';
import { format, formatDistanceToNow } from 'date-fns';

const CallDetail = () => {
  const { id } = useParams();
  const [call, setCall] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('transcript');

  useEffect(() => {
    fetchCallDetail();
  }, [id]);

  const fetchCallDetail = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No token found');
        window.location.href = '/login';
        return;
      }
      
      const response = await axios.get(`/api/calls/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCall(response.data);
    } catch (error) {
      console.error('Error fetching call:', error);
      if (error.response?.status === 401) {
        // Token expired, redirect to login
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/login';
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        window.location.href = '/login';
        return;
      }
      
      await axios.post(`/api/calls/${id}/refresh`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchCallDetail();
    } catch (error) {
      console.error('Error refreshing:', error);
      if (error.response?.status === 401) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/login';
      }
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'ongoing': return <span className="badge-info">Ongoing</span>;
      case 'ended': return <span className="badge-success">Completed</span>;
      case 'analyzed': return <span className="badge-success">Analyzed</span>;
      case 'error': return <span className="badge-error">Error</span>;
      default: return <span className="badge-warning">{status}</span>;
    }
  };

  const getDirectionIcon = (direction) => {
    return direction === 'inbound' ? (
      <span className="flex items-center gap-1 text-green-600">
        <PhoneIncoming className="w-4 h-4" />
        Inbound
      </span>
    ) : (
      <span className="flex items-center gap-1 text-blue-600">
        <PhoneOutgoing className="w-4 h-4" />
        Outbound
      </span>
    );
  };

  // Parse transcript safely
  const getTranscript = () => {
    if (!call?.transcript) return [];
    
    // If it's already an array, return it
    if (Array.isArray(call.transcript)) {
      return call.transcript;
    }
    
    // If it's a string, try to parse it
    if (typeof call.transcript === 'string') {
      // Check if it looks like JSON (starts with [ or {)
      const trimmed = call.transcript.trim();
      if (trimmed.startsWith('[') || trimmed.startsWith('{')) {
        try {
          const parsed = JSON.parse(trimmed);
          return Array.isArray(parsed) ? parsed : [];
        } catch (e) {
          // Not valid JSON, return as plain text message
          console.log('Transcript is not JSON, treating as plain text');
          return [{ role: 'agent', content: trimmed }];
        }
      } else {
        // Plain text, return as single message
        return [{ role: 'agent', content: trimmed }];
      }
    }
    
    // If it's an object (but not array), try to convert to array
    if (typeof call.transcript === 'object') {
      // Handle case where transcript might be { transcript: [...] }
      if (call.transcript.transcript && Array.isArray(call.transcript.transcript)) {
        return call.transcript.transcript;
      }
      // Handle case where it's a single object
      return [call.transcript];
    }
    
    return [];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!call) {
    return (
      <div className="text-center py-12">
        <Phone className="w-16 h-16 text-gray-300 mx-auto mb-4" />
        <h2 className="text-2xl font-bold text-gray-700 mb-2">Call not found</h2>
        <Link to="/calls" className="btn-primary">Back to Calls</Link>
      </div>
    );
  }

  const transcript = getTranscript();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link to="/calls" className="btn-secondary p-2">
          <ArrowLeft className="w-5 h-5" />
        </Link>
        <div className="flex-1">
          <h1 className="text-3xl font-bold text-gray-900 mb-1">Call Details</h1>
          <p className="text-gray-500">{call.call_id}</p>
        </div>
        <div className="flex items-center gap-3">
          <button 
            onClick={handleRefresh}
            className="btn-secondary flex items-center gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
          {call.recording_url && (
            <a 
              href={`/api/calls/${call.call_id}/recording`}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary flex items-center gap-2"
            >
              <Download className="w-4 h-4" />
              Download Recording
            </a>
          )}
        </div>
      </div>

      {/* Call Info Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Caller Info */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-100 to-indigo-100 flex items-center justify-center">
              <User className="w-5 h-5 text-blue-600" />
            </div>
            <span className="text-gray-500 text-sm">Caller</span>
          </div>
          <p className="text-xl font-bold text-gray-900">{call.caller_name || 'Unknown'}</p>
          <p className="text-gray-500">{call.caller_number || 'No number'}</p>
          {call.to_number && (
            <p className="text-gray-400 text-sm mt-1">→ {call.to_number}</p>
          )}
        </div>

        {/* Direction & Status */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-green-100 to-emerald-100 flex items-center justify-center">
              <Phone className="w-5 h-5 text-green-600" />
            </div>
            <span className="text-gray-500 text-sm">Direction</span>
          </div>
          <div className="mb-2">{getDirectionIcon(call.direction)}</div>
          <div>{getStatusBadge(call.call_status)}</div>
        </div>

        {/* Duration & Cost */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-100 to-orange-100 flex items-center justify-center">
              <Clock className="w-5 h-5 text-amber-600" />
            </div>
            <span className="text-gray-500 text-sm">Duration</span>
          </div>
          <p className="text-xl font-bold text-gray-900">
            {call.duration ? (
              `${Math.floor(call.duration / 60)}m ${call.duration % 60}s`
            ) : (
              '-'
            )}
          </p>
          {call.cost !== null && call.cost !== undefined && (
            <p className="text-green-600 font-medium flex items-center gap-1 mt-1">
              <DollarSign className="w-4 h-4" />
              {parseFloat(call.cost).toFixed(4)}
            </p>
          )}
        </div>

        {/* Date & Agent */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-100 to-purple-100 flex items-center justify-center">
              <FileText className="w-5 h-5 text-violet-600" />
            </div>
            <span className="text-gray-500 text-sm">Date</span>
          </div>
          <p className="text-xl font-bold text-gray-900">
            {call.start_time ? format(new Date(call.start_time), 'MMM d, yyyy') : '-'}
          </p>
          <p className="text-gray-500 text-sm">
            {call.start_time && formatDistanceToNow(new Date(call.start_time), { addSuffix: true })}
          </p>
          {call.agent_id && (
            <p className="text-blue-600 text-sm mt-2 flex items-center gap-1">
              <UserCircle className="w-4 h-4" />
              Agent: {call.agent_id.substring(0, 8)}...
            </p>
          )}
        </div>
      </div>

      {/* Audio Player */}
      {call.recording_url && (
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Play className="w-5 h-5 text-blue-600" />
            Call Recording
          </h3>
          <audio 
            controls 
            className="w-full"
            src={call.recording_url}
          >
            Your browser does not support the audio element.
          </audio>
        </div>
      )}

      {/* Tabs */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="flex border-b border-gray-200">
          <button
            onClick={() => setActiveTab('transcript')}
            className={`flex items-center gap-2 px-6 py-4 font-medium transition-colors ${
              activeTab === 'transcript' 
                ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50' 
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
            }`}
          >
            <MessageSquare className="w-4 h-4" />
            Transcript
          </button>
          <button
            onClick={() => setActiveTab('summary')}
            className={`flex items-center gap-2 px-6 py-4 font-medium transition-colors ${
              activeTab === 'summary' 
                ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50' 
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
            }`}
          >
            <FileText className="w-4 h-4" />
            Summary
          </button>
        </div>

        <div className="p-6">
          {activeTab === 'transcript' && (
            <div className="space-y-4">
              {transcript.length > 0 ? (
                transcript.map((entry, index) => (
                  <div 
                    key={index} 
                    className={`flex gap-4 ${entry.role === 'agent' ? 'flex-row' : 'flex-row-reverse'}`}
                  >
                    <div className={`max-w-[80%] rounded-2xl p-4 ${
                      entry.role === 'agent' 
                        ? 'bg-blue-50 text-blue-900 border border-blue-100' 
                        : 'bg-gray-100 text-gray-900 border border-gray-200'
                    }`}>
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xs font-semibold uppercase tracking-wider opacity-70">
                          {entry.role || 'Unknown'}
                        </span>
                        {entry.timestamp && (
                          <span className="text-xs opacity-50">
                            {entry.timestamp}
                          </span>
                        )}
                      </div>
                      <p className="leading-relaxed">
                        {entry.content || entry.text || entry.message || String(entry)}
                      </p>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-12">
                  <MessageSquare className="w-16 h-16 text-gray-200 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-gray-700 mb-2">No transcript available</h3>
                  <p className="text-gray-500">Transcript will appear here once the call is processed</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'summary' && (
            <div className="space-y-6">
              {call.summary ? (
                <div className="prose max-w-none">
                  <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                    <h3 className="text-lg font-semibold text-blue-600 mb-4">Call Summary</h3>
                    <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">{call.summary}</p>
                  </div>
                  
                  {call.call_analysis?.call_summary && (
                    <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                      <h3 className="text-lg font-semibold text-blue-600 mb-4">Detailed Analysis</h3>
                      <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">{call.call_analysis.call_summary}</p>
                    </div>
                  )}
                  
                  {call.sentiment && (
                    <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                      <h3 className="text-lg font-semibold text-blue-600 mb-4">Sentiment Analysis</h3>
                      <div className="flex items-center gap-3">
                        <div className={`w-3 h-3 rounded-full ${
                          call.sentiment === 'positive' ? 'bg-green-500' :
                          call.sentiment === 'negative' ? 'bg-red-500' :
                          'bg-yellow-500'
                        }`} />
                        <span className="text-gray-700 capitalize">{call.sentiment}</span>
                      </div>
                    </div>
                  )}

                  {call.call_analysis?.user_sentiment && (
                    <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                      <h3 className="text-lg font-semibold text-blue-600 mb-4">User Sentiment</h3>
                      <div className="flex items-center gap-3">
                        <div className={`w-3 h-3 rounded-full ${
                          call.call_analysis.user_sentiment === 'Positive' ? 'bg-green-500' :
                          call.call_analysis.user_sentiment === 'Negative' ? 'bg-red-500' :
                          'bg-yellow-500'
                        }`} />
                        <span className="text-gray-700">{call.call_analysis.user_sentiment}</span>
                      </div>
                    </div>
                  )}

                  {call.disconnection_reason && (
                    <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                      <h3 className="text-lg font-semibold text-blue-600 mb-4">Disconnection Reason</h3>
                      <p className="text-gray-700">{call.disconnection_reason}</p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-12">
                  <FileText className="w-16 h-16 text-gray-200 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-gray-700 mb-2">No summary available</h3>
                  <p className="text-gray-500">Summary will appear here once the call is analyzed</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CallDetail;